import { Flex, Form, Select, Button, Input } from 'antd';
import { uniqBy } from 'lodash';
import { isCryptoCurrency } from 'features/bill/lib';
import React from 'react';
import { NetworkLabel } from 'widgets/Invoices/InvoiceGenerateForm.PaymentTypeSelection';
import { CheckIcon } from '@heroicons/react/24/outline';
import { CurrencyIcon } from 'features/payments/CurrencyIcon';

/** We handle only Symbol change, currencyTYpe still the same in the parent component */

export const CurrencySelect: React.FC<{
  currencySymbol?: string;
  network?: string;
  currenciesByNetworks?: Record<string, { symbol: string }[]>;
  onSelect?: (values: { currency: string; network?: string }) => void;
}> = ({ currencySymbol, currenciesByNetworks, network, onSelect }) => {
  const [form] = Form.useForm();
  const networkList = React.useMemo(() => {
    return currenciesByNetworks
      ? Object.keys(currenciesByNetworks).filter(
          (network) => network !== 'fiat'
        )
      : [];
  }, [currenciesByNetworks]);

  const currencySymbols = uniqBy(
    Object.keys(currenciesByNetworks || {}).flatMap(
      (network) => currenciesByNetworks[network]
    ),
    'symbol'
  );

  const handleConfirm = () => {
    const values = form.getFieldsValue();
    onSelect?.(values);
  };

  React.useEffect(() => {
    if (currencySymbol && network) {
      form.setFieldsValue({ currency: currencySymbol, network });
    }
  }, [currencySymbol, network, form]);

  return (
    <Form
      className="w-72"
      form={form}
      initialValues={{ currency: currencySymbol, network }}
      layout="vertical">
      <Flex vertical justify="flex-end" gap={'middle'}>
        <Form.Item style={{ margin: 0 }} name="currency">
          <Select
            onChange={(value) => {
              const networks = Object.keys(currenciesByNetworks || {});
              const availableNetworks = networks.filter((net) =>
                currenciesByNetworks[net].some((c) => c.symbol === value)
              );

              if (
                availableNetworks.length > 0 &&
                !availableNetworks.includes(form.getFieldValue('network'))
              ) {
                form.setFieldsValue({ network: availableNetworks[0] });
              }
            }}
            options={currencySymbols.map((c) => {
              return {
                value: c.symbol,
                label: (
                  <Flex align={'center'} gap={'small'}>
                    <CurrencyIcon currency={c.symbol.toUpperCase()} />
                    {c.symbol.toUpperCase()}
                  </Flex>
                ),
              };
            })}
          />
        </Form.Item>

        <Form.Item style={{ margin: 0 }} dependencies={['currency']} noStyle>
          {() => {
            if (isCryptoCurrency(form.getFieldValue('currency'))) {
              return (
                <Form.Item style={{ margin: 0 }} name="network">
                  <Select
                    options={networkList.map((n) => {
                      return {
                        value: n,
                        label: <NetworkLabel network={n} />,
                      };
                    })}
                    onChange={(value) => {
                      const currentCurrency = form.getFieldValue('currency');
                      const currenciesForNetwork =
                        currenciesByNetworks[value] || [];

                      if (
                        !currenciesForNetwork.some(
                          (currency) => currency.symbol === currentCurrency
                        )
                      ) {
                        const firstAvailableCurrency =
                          currenciesForNetwork[0]?.symbol;
                        if (firstAvailableCurrency) {
                          form.setFieldsValue({
                            currency: firstAvailableCurrency,
                          });
                        }
                      }
                    }}
                  />
                </Form.Item>
              );
            }

            return (
              <Form.Item name="network" hidden={true}>
                <Input type="hidden" />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item style={{ margin: 0 }}>
          <Button
            type="primary"
            className="bg-indigo-600 w-[100%] hover:!bg-indigo-500"
            onClick={handleConfirm}
            icon={<CheckIcon className="w-4" />}
            block>
            Confirm
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};
